import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentOutlined } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import PageHeader from 'shared/components/page-header/page-header';
import PopUp from 'shared/components/pop-up/pop-up';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import { TableOverflowMenuItem } from 'shared/components/table/hooks';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import LocationRow from 'features/connectors/components/location-row.component';
import { useConnectorsContextProvider } from 'features/connectors/context/connectors-provider';
import { Connector, ConnectorStatus, ConnectorType } from 'features/connectors/domain/connector';
import { ConnectorCannotDeleteReason } from 'features/connectors/domain/connector-cannot-delete-reason';
import ConnectorFilters from 'features/connectors/views/connector-filters/connector-filters';
import styles from 'features/connectors/views/connectors.module.scss';
import useFeatureSubscriptionFeatureFlag from 'features/feature-subscriptions/hooks/feature-subscription-feature-flag';
import IntegrationsNavigation from 'features/integrations/views/integrations-navigation';
import ConnectorDetailsPopup from './connector-details/connector-details-popup.component';

const Connectors = () => {
  const { t } = useTranslation('connectors');
  const { hasPermission } = usePermissionAuthorization();
  const { isCareConnectEnabled } = useFeatureSubscriptionFeatureFlag();

  const {
    connectorsResponse,
    isLoading,
    isSuccess,
    connectorsFilterHook,
    connectorDetailsHook,
    openDetailsInViewingModeAndRefresh,
    openDetailsInEditingModeAndRefresh,
    openDetailsInCreationMode,
  } = useConnectorsContextProvider();

  const { connectors, total } = connectorsResponse ?? {
    connectors: [],
    total: 0,
  };
  const showConnectorsTable = !isLoading && isSuccess;

  const locationRows = (connector: Connector) => {
    return (
      <>
        {connector.type === ConnectorType.NedapOns && (
          <>
            {connector.organisationUnits.map((unit) => (
              <LocationRow key={unit.id} name={unit.name} />
            ))}
          </>
        )}
        {connector.type === ConnectorType.Vcare && <Typography variant="subtitle1">{connector.organisationUnits[0]?.name}</Typography>}
      </>
    );
  };

  const getIcons = (connector: Connector): JSX.Element[] => {
    const result: JSX.Element[] = [];

    result.push(
      <CommentOutlined
        data-testid="noteIcon"
        key={'note-icon'}
        sx={{
          width: 16,
          height: 16,
          color: Constants.Colors.onSurfaceVariant,
          visibility: connector.note ? 'visible' : 'hidden',
        }}
      />,
    );

    if (connector.status === ConnectorStatus.offline) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.ConnectorOffline}`}
          type={MonitoringType.ConnectorOffline}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    } else if (connector.status === ConnectorStatus.online) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.ConnectorOnline}`}
          type={MonitoringType.ConnectorOnline}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    }

    return result;
  };

  const columns: ColumnDefinition<Connector, NestedKeyof<Connector>>[] = [
    { key: 'name', label: t('table.column.name') },
    {
      key: 'type',
      label: t('table.column.type'),
      renderCustomContentProvider: ({ type }) => <>{t(`details.information.type.${type}`)}</>,
      disableSort: true,
    },

    {
      key: 'organisationUnits',
      label: t('table.column.organisationUnit'),
      renderCustomContentProvider: locationRows,
      disableSort: true,
      tableCellProps: { width: '400px' },
    },
    {
      key: 'note',
      label: '',
      renderCustomContentProvider: (connector) => <div className={styles.iconRow}>{getIcons(connector)}</div>,
      disableSort: true,
      tableCellProps: {
        align: 'right',
        width: '24px',
      },
    },
  ];

  const getOverflowMenuItems = (): Array<TableOverflowMenuItem<Connector>> | undefined => {
    const menuItems: Array<TableOverflowMenuItem<Connector>> = [
      {
        label: t('overflowMenu.view'),
        action: (connector) => openDetailsInViewingModeAndRefresh(connector),
      },
    ];

    if (hasPermission(Permission.UpdateConnectors)) {
      menuItems.push({
        label: t('overflowMenu.edit'),
        action: (connector) => openDetailsInEditingModeAndRefresh(connector),
      });
    }
    if (hasPermission(Permission.DeleteConnectors)) {
      menuItems.push(
        { label: 'divider', isVisible: (connector) => connector.type === ConnectorType.Vcare },
        {
          label: t('overflowMenu.delete'),
          action: (connector) => connectorDetailsHook.deleteConnector(connector),
          isVisible: (connector) => connector.type === ConnectorType.Vcare,
        },
      );
    }

    return menuItems;
  };

  const isAddButtonVisible = (): boolean => isCareConnectEnabled && hasPermission(Permission.CreateConnectors);

  return (
    <div className={styles.container}>
      <PageHeader
        title={t('pageTitle')}
        navigationComponent={<IntegrationsNavigation />}
        filterComponent={<ConnectorFilters />}
        addBtnVisible={isAddButtonVisible()}
        onAdd={openDetailsInCreationMode}
      />
      {isLoading && <LoadingIndicator />}
      {showConnectorsTable && (
        <Table
          data={connectors.map<RowItem<Connector>>((connector) => ({
            data: connector,
          }))}
          columns={columns}
          onItemClick={(connector: Connector) => openDetailsInViewingModeAndRefresh(connector)}
          rowIdentifier={(item) => item.id ?? ''}
          initialOrderBy={connectorsFilterHook.table.sortFromSessionStorage?.property}
          initialOrderDirection={connectorsFilterHook.table.sortFromSessionStorage?.isAscending ? 'asc' : 'desc'}
          useBuiltInSorter
          pagination={{
            count: total,
            onPageChanged: connectorsFilterHook.table.handleOnPageChanged,
            rowsPerPage: connectorsFilterHook.table.currentRowsPerPage,
            page: connectorsFilterHook.table.currentPage,
            onRowsPerPageChanged: connectorsFilterHook.table.handleOnRowsPerPageChanged,
          }}
          onSortChanged={connectorsFilterHook.table.handleOnSortChanged}
          overflowMenuOptions={getOverflowMenuItems()}
        />
      )}
      {connectorDetailsHook.isDetailsOpen && <ConnectorDetailsPopup />}
      <PopUp
        isOpen={connectorDetailsHook.isDeleteConfirmationPopupOpen}
        title={t('deleteConnectorConfirmation.title')}
        body={t('deleteConnectorConfirmation.bodyText', {
          connectorName: connectorDetailsHook.currentSelectedConnector?.name,
        })}
        primaryButtonText={t('deleteConnectorConfirmation.confirmButton')}
        secondaryButtonText={t('deleteConnectorConfirmation.cancelButton')}
        handleOnClose={connectorDetailsHook.closeDeleteConfirmationPopup}
        secondaryButtonAction={connectorDetailsHook.closeDeleteConfirmationPopup}
        primaryButtonAction={() => connectorDetailsHook.deleteConfirmationAction?.()}
        isLoadingPrimaryAction={connectorDetailsHook.mutationIsLoading}
      />
      <PopUp
        isOpen={connectorDetailsHook.isCannotDeletePopupOpen}
        title={t('cannotDeleteConnector.title', {
          connectorName: connectorDetailsHook.currentSelectedConnector?.name,
        })}
        body={
          connectorDetailsHook.currentSelectedConnector?.cannotDeleteReason === ConnectorCannotDeleteReason.ActiveCareConnectSubscriptions
            ? t('cannotDeleteConnector.bodyTextActiveSubscriptions')
            : t('cannotDeleteConnector.bodyTextActiveCareConnectSettings')
        }
        primaryButtonText={t('cannotDeleteConnector.closeButton')}
        handleOnClose={connectorDetailsHook.closeCannotDeletePopup}
        primaryButtonAction={connectorDetailsHook.closeCannotDeletePopup}
      />
    </div>
  );
};

export default Connectors;
